/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 18/05/22
 */

import {
    withCurrentAssignment,
    WithCurrentAssignmentProps,
} from '@/components/contexts/AssignmentContext/withCurrentAssignment';
import GlobalAdminListTable from '@/components/pages/Admin/Global/GlobalAdminListTable';
import UserAssignmentPicker from '@/components/pages/Admin/Pickers/UserAssignmentPicker';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { User } from '@/wpt-lib/interfaces/User';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { useGetRequest, usePutRequest } from '@/utils/useStatefulRequests';
import { Text, VStack } from '@chakra-ui/react';
import React, { useLayoutEffect, useState } from 'react';
import { Tile } from '@/components/Tile';

interface UpdateGlobalAdminsDTO {
    adminAssignmentIds: number[];
}

const GlobalAdminEditor = ({ currentAssignment }: WithCurrentAssignmentProps) => {
    const { error, loading, responseData: globalAdmins, makeRequest } = useGetRequest<ListResponse<Assignment>>();
    const {
        error: submitAdminsError,
        loading: submittingAdmins,
        makeRequest: updateAdmins,
        responseData: updatedAdmins,
    } = usePutRequest<Assignment[], UpdateGlobalAdminsDTO>();

    const [tempAdminAssignmentsList, setTempAdminAssignmentsList] = useState<Assignment[]>();

    useAsyncLayoutEffect(async () => {
        try {
            await makeRequest(`assignment/admins`, {
                params: {
                    assignment: currentAssignment?.id,
                },
            });
        } catch (e) {
            // handled by hook
        }
    }, [currentAssignment]);

    useLayoutEffect(() => {
        if (updatedAdmins) {
            setTempAdminAssignmentsList(updatedAdmins.map((assignment) => assignment));
        } else if (globalAdmins) {
            setTempAdminAssignmentsList(globalAdmins.items.map((assignment) => assignment));
        }
    }, [globalAdmins, updatedAdmins]);

    const handleSubmit = async (assignments: Assignment[]) => {
        await updateAdmins(
            `assignment/admins`,
            {
                adminAssignmentIds: assignments.map((assignment) => assignment.id),
            },
            {
                params: {
                    assignment: currentAssignment?.id,
                },
            },
        );
    };

    const onAddAssignment = async (selectedAssignment: Assignment, selectedUser: User): Promise<boolean> => {
        if (!tempAdminAssignmentsList || !tempAdminAssignmentsList.find((a) => a.id === selectedAssignment.id)) {
            try {
                await handleSubmit([
                    ...(tempAdminAssignmentsList || []),
                    { ...selectedAssignment, user: selectedUser },
                ]);
                return true;
            } catch (e) {
                // handled by hook
            }
        }
        return false;
    };

    const onRemoveAssignment = async (assignment: Assignment) => {
        try {
            await handleSubmit(
                (tempAdminAssignmentsList || []).filter((adminAssignment) => adminAssignment.id !== assignment.id),
            );
        } catch (e) {
            // handled by hook
        }
    };

    return (
        <Tile
            colspan={3}
            heading={'University Wide Administrators'}
            collapsable
            rounded={'lg'}
            borderWidth={1}
            borderColor={'gray.100'}
        >
            <VStack alignItems={'flex-start'} spacing={3}>
                <Text>This section allows you to add university administrators.</Text>
                <StateDisplay
                    data={tempAdminAssignmentsList}
                    loading={loading}
                    error={!!error}
                    errorRender={() => <SimpleAlert variant="subtle">Could not load Administrators</SimpleAlert>}
                    render={(data) => {
                        return (
                            <>
                                <UserAssignmentPicker
                                    currentAssignment={currentAssignment}
                                    loading={submittingAdmins}
                                    onSelectAssignment={onAddAssignment}
                                />
                                <GlobalAdminListTable
                                    admins={data}
                                    onRemove={onRemoveAssignment}
                                    actionsDisabled={submittingAdmins}
                                    currentAssignment={currentAssignment}
                                />
                                {submitAdminsError && (
                                    <SimpleAlert variant="subtle">Could not update administrators.</SimpleAlert>
                                )}
                            </>
                        );
                    }}
                />
            </VStack>
        </Tile>
    );
};

export default withCurrentAssignment(GlobalAdminEditor);
